import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { UploadCloud, Trash2, Plus, FileText, BookOpen, Pencil, ShoppingBag, ExternalLink, Truck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";

const FALLBACK = "https://images.pexels.com/photos/11911071/pexels-photo-11911071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400";

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-neutral-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-2">Dashboard</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#0F172A] tracking-tight">Shop Manager</h1>
        <p className="text-sm text-neutral-500 mt-2">Add or remove books and assignments. Changes are live immediately.</p>
      </div>

      <Tabs defaultValue="books" className="w-full">
        <TabsList className="mb-8" data-testid="admin-tabs">
          <TabsTrigger value="books" data-testid="tab-books"><BookOpen size={14} className="mr-2"/>Books</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments"><FileText size={14} className="mr-2"/>Assignments</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingBag size={14} className="mr-2"/>Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="books">
          <BooksManager />
        </TabsContent>
        <TabsContent value="assignments">
          <AssignmentsManager />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersList />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BooksManager() {
  const [books, setBooks] = useState([]);
  const [counter, setCounter] = useState(null);
  const [form, setForm] = useState({ title: "", author: "", price: "", category: "", description: "" });
  const [cover, setCover] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const loadCounter = useCallback(
    () => api.get("/books/count").then((r) => setCounter(r.data)).catch(() => {}),
    []
  );
  const load = useCallback(async () => {
    await Promise.all([
      api.get("/books").then((r) => setBooks(r.data)).catch(() => {}),
      loadCounter(),
    ]);
  }, [loadCounter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.author || !form.price || !form.category) {
      toast.error("Title, author, price and category are required");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (cover) fd.append("cover", cover);
    try {
      await api.post("/books", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Book added");
      setForm({ title: "", author: "", price: "", category: "", description: "" });
      setCover(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add book");
    } finally { setSubmitting(false); }
  };

  const removeBook = async (id) => {
    try {
      await api.delete(`/books/${id}`);
      toast.success("Book deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-8">
      <form onSubmit={submit} data-testid="add-book-form" className="col-span-12 lg:col-span-5 border border-neutral-200 bg-white rounded-2xl p-6">
        <div className="font-serif text-2xl text-[#0F172A] mb-1">Add a book</div>
        <div className="text-xs text-neutral-500 mb-6">Fill in the details and optionally upload a cover.</div>

        <div className="grid grid-cols-2 gap-4">
          <FieldWrap label="Title" full>
            <Input data-testid="book-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </FieldWrap>
          <FieldWrap label="Author">
            <Input data-testid="book-author-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Category">
            <Input data-testid="book-category-input" placeholder="e.g. Class 10" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Price (₹)">
            <Input data-testid="book-price-input" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Cover">
            <Input data-testid="book-cover-input" ref={fileRef} type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} />
          </FieldWrap>
          <FieldWrap label="Description" full>
            <Textarea data-testid="book-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FieldWrap>
        </div>

        <Button
          type="submit"
          data-testid="submit-book-btn"
          disabled={submitting}
          className="mt-6 w-full h-11 rounded-full bg-[#4A6741] hover:bg-[#3a5233] text-white transition-colors"
          style={{ transitionProperty: "background-color" }}
        >
          <Plus size={16} className="mr-2"/> {submitting ? "Adding..." : "Add Book"}
        </Button>
      </form>

      <div className="col-span-12 lg:col-span-7">
        <div className="flex items-center justify-between mb-4">
          <div className="font-serif text-2xl text-[#0F172A]">Book inventory</div>
          <div data-testid="admin-books-counter" className="text-sm text-neutral-600">
            Titles in stock:{" "}
            <span className="font-medium text-[#0F172A]">
              {counter ? counter.displayed.toLocaleString() : "…"}
            </span>
          </div>
        </div>
        {books.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500 text-sm">
            No books yet — add your first one.
          </div>
        ) : (
          <div className="space-y-3">
            {books.map((b) => (
              <div key={b.id} data-testid={`admin-book-${b.id}`} className="flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-3">
                <img
                  src={b.cover_path ? `${API}/files/book-cover/${b.id}` : FALLBACK}
                  onError={(e) => { e.currentTarget.src = FALLBACK; }}
                  alt=""
                  loading="lazy"
                  className="w-14 h-16 object-cover rounded-md bg-neutral-100"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg text-[#0F172A] leading-tight truncate">{b.title}</div>
                  <div className="text-xs text-neutral-500 truncate">{b.author} · {b.category}</div>
                  <div className="text-sm text-[#E05A47]">₹ {Number(b.price).toFixed(0)}</div>
                </div>
                <EditBookDialog book={b} onSaved={load} />
                <ConfirmDelete label={`Delete "${b.title}"?`} onConfirm={() => removeBook(b.id)} testId={`delete-book-${b.id}`} />
              </div>
            ))}
            <p data-testid="admin-books-fetch-note" className="text-xs text-neutral-500 italic pt-2 text-center">
              Not all books can be shown. Please fetch MongoDB viewer for the full database.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function AssignmentsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", subject: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/assignments").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !file) {
      toast.error("Title and file are required");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("subject", form.subject);
    fd.append("file", file);
    try {
      await api.post("/assignments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Assignment uploaded");
      setForm({ title: "", subject: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/assignments/${id}`);
      toast.success("Assignment deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-8">
      <form onSubmit={submit} data-testid="upload-assignment-form" className="col-span-12 lg:col-span-5 border border-neutral-200 bg-white rounded-2xl p-6">
        <div className="font-serif text-2xl text-[#0F172A] mb-1">Upload assignment</div>
        <div className="text-xs text-neutral-500 mb-6">PDF, DOCX or image files.</div>

        <div className="space-y-4">
          <FieldWrap label="Title" full>
            <Input data-testid="assignment-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Subject" full>
            <Input data-testid="assignment-subject-input" placeholder="e.g. Class 10 Science" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </FieldWrap>

          <label
            data-testid="assignment-file-dropzone"
            className="block border-2 border-dashed border-neutral-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#4A6741] transition-colors"
            style={{ transitionProperty: "border-color" }}
          >
            <UploadCloud size={22} className="mx-auto text-neutral-400"/>
            <div className="mt-2 text-sm text-neutral-700">
              {file ? file.name : "Click to select a file (PDF, DOCX, image)"}
            </div>
            <input
              ref={fileRef}
              data-testid="assignment-file-input"
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
        </div>

        <Button
          type="submit"
          data-testid="submit-assignment-btn"
          disabled={submitting}
          className="mt-6 w-full h-11 rounded-full bg-[#4A6741] hover:bg-[#3a5233] text-white transition-colors"
          style={{ transitionProperty: "background-color" }}
        >
          <UploadCloud size={16} className="mr-2"/> {submitting ? "Uploading..." : "Upload"}
        </Button>
      </form>

      <div className="col-span-12 lg:col-span-7">
        <div className="flex items-center justify-between mb-4">
          <div className="font-serif text-2xl text-[#0F172A]">Assignments</div>
          <div className="text-xs text-neutral-500">{items.length} total</div>
        </div>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500 text-sm">
            No assignments yet.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <div key={a.id} data-testid={`admin-assignment-${a.id}`} className="flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-[#F1EDE7] text-[#4A6741] flex items-center justify-center shrink-0">
                  <FileText size={18}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#0F172A] leading-tight truncate">{a.title}</div>
                  <div className="text-xs text-neutral-500 truncate">{a.subject || "General"} · {a.original_filename}</div>
                </div>
                <a
                  href={`${API}/files/assignment/${a.id}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`admin-view-assignment-${a.id}`}
                  className="text-xs text-neutral-600 hover:text-[#E05A47] px-2"
                >
                  View
                </a>
                <ConfirmDelete label={`Delete "${a.title}"?`} onConfirm={() => remove(a.id)} testId={`delete-assignment-${a.id}`}/>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldWrap({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ConfirmDelete({ label, onConfirm, testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid={testId}
          className="p-2 rounded-md text-neutral-400 hover:text-white hover:bg-[#E05A47] transition-colors"
          style={{ transitionProperty: "background-color, color" }}
          aria-label="Delete"
        >
          <Trash2 size={16}/>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{label}</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm} className="bg-[#E05A47] hover:bg-[#c94a38]">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function EditBookDialog({ book, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: book.title || "",
    author: book.author || "",
    price: String(book.price ?? ""),
    category: book.category || "",
    description: book.description || "",
  });
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.author || !form.price || !form.category) {
      toast.error("Title, author, price and category are required");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (cover) fd.append("cover", cover);
    try {
      await api.put(`/books/${book.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Book updated");
      setOpen(false);
      setCover(null);
      if (fileRef.current) fileRef.current.value = "";
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid={`edit-book-${book.id}`}
        onClick={() => setOpen(true)}
        className="p-2 rounded-md text-neutral-400 hover:text-white hover:bg-[#4A6741] transition-colors"
        style={{ transitionProperty: "background-color, color" }}
        aria-label="Edit"
      >
        <Pencil size={16}/>
      </button>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit book</DialogTitle>
          <DialogDescription>Update details. Uploading a new cover replaces the existing one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} data-testid={`edit-book-form-${book.id}`} className="grid grid-cols-2 gap-4">
          <FieldWrap label="Title" full>
            <Input data-testid={`edit-book-title-${book.id}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </FieldWrap>
          <FieldWrap label="Author">
            <Input data-testid={`edit-book-author-${book.id}`} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Category">
            <Input data-testid={`edit-book-category-${book.id}`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Price (₹)">
            <Input data-testid={`edit-book-price-${book.id}`} type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required/>
          </FieldWrap>
          <FieldWrap label="Replace cover (optional)">
            <Input data-testid={`edit-book-cover-${book.id}`} ref={fileRef} type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} />
          </FieldWrap>
          <FieldWrap label="Description" full>
            <Textarea data-testid={`edit-book-description-${book.id}`} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FieldWrap>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid={`edit-book-cancel-${book.id}`}>Cancel</Button>
            <Button
              type="submit"
              data-testid={`edit-book-save-${book.id}`}
              disabled={saving}
              className="bg-[#4A6741] hover:bg-[#3a5233] text-white transition-colors"
              style={{ transitionProperty: "background-color" }}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // per-order in-flight action key

  const load = useCallback(() => {
    setLoading(true);
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createShipment = async (orderId) => {
    setBusy((b) => ({ ...b, [orderId]: "create" }));
    try {
      await api.post(`/orders/${orderId}/create-shipment`);
      toast.success("Shipment created in Shiprocket");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Shiprocket create failed");
    } finally {
      setBusy((b) => ({ ...b, [orderId]: null }));
    }
  };

  const refreshTracking = async (orderId) => {
    setBusy((b) => ({ ...b, [orderId]: "track" }));
    try {
      await api.get(`/orders/${orderId}/tracking`);
      toast.success("Tracking updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not fetch tracking");
    } finally {
      setBusy((b) => ({ ...b, [orderId]: null }));
    }
  };

  const paidCount = orders.filter((o) => o.status === "paid").length;
  const totalRevenue = orders.filter((o) => o.status === "paid")
    .reduce((sum, o) => sum + (Number(o.amount_inr) || 0), 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label="Total orders" value={orders.length}/>
        <StatCard label="Paid" value={paidCount}/>
        <StatCard label="Revenue" value={`₹ ${totalRevenue.toFixed(0)}`} accent/>
      </div>
      {loading ? (
        <div className="text-neutral-500 text-sm">Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500 text-sm">
          No orders yet — customers haven't purchased any books online.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif text-lg text-[#0F172A] leading-tight">{o.book_title}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Qty {o.quantity} · ₹{Number(o.amount_inr).toFixed(0)} · {new Date(o.created_at).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={o.status}/>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Customer</div>
                  <div className="mt-1 text-neutral-800">{o.customer?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Phone</div>
                  <a href={`tel:${o.customer?.phone}`} className="mt-1 text-neutral-800 hover:text-[#E05A47] inline-block">
                    {o.customer?.phone || "—"}
                  </a>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Address</div>
                  <div className="mt-1 text-neutral-800 text-xs">
                    {o.customer?.address || o.customer?.pincode ? (
                      <>
                        {o.customer?.address}
                        {(o.customer?.city || o.customer?.state || o.customer?.pincode) && (
                          <div className="text-neutral-500">
                            {[o.customer?.city, o.customer?.state, o.customer?.pincode].filter(Boolean).join(", ")}
                          </div>
                        )}
                      </>
                    ) : <span className="text-neutral-400">Pickup at shop</span>}
                  </div>
                </div>
              </div>
              {typeof o.shipping_fee_inr === "number" && (
                <div data-testid={`order-shipping-${o.id}`} className="mt-3 text-xs text-neutral-600">
                  Shipping charged: <span className="font-medium text-[#0F172A]">₹{Number(o.shipping_fee_inr).toFixed(0)}</span>
                  {o.shipping_courier ? ` via ${o.shipping_courier}` : ""}
                  {o.shipping_source === "fallback" ? " (standard flat rate)" : ""}
                  {Number(o.shipping_fee_inr) === 0 ? " — store pickup" : ""}
                </div>
              )}
              {o.customer?.notes && (
                <div className="mt-3 text-xs text-neutral-500 italic">Note: {o.customer.notes}</div>
              )}

              {/* Shiprocket panel */}
              {(o.status === "paid" || o.status === "cod_pending") && (
                <div data-testid={`shipment-panel-${o.id}`} className="mt-4 pt-4 border-t border-neutral-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck size={14} className="text-[#4A6741]"/>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-medium">Shiprocket</span>
                  </div>

                  {o.shiprocket_error && !o.shiprocket?.shipment_id && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2 mb-3 break-all">
                      {o.shiprocket_error}
                    </div>
                  )}

                  {!o.shiprocket?.shipment_id ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-neutral-500">
                        {o.customer?.pincode
                          ? "Shipment not created yet."
                          : "No pincode — store pickup order (no shipment needed)."}
                      </div>
                      {o.customer?.pincode && (
                        <Button
                          size="sm"
                          data-testid={`create-shipment-${o.id}`}
                          onClick={() => createShipment(o.id)}
                          disabled={busy[o.id] === "create"}
                          className="rounded-full bg-[#4A6741] hover:bg-[#3a5233] text-white h-8 text-xs"
                        >
                          <Truck size={12} className="mr-1.5"/>
                          {busy[o.id] === "create" ? "Creating..." : "Create shipment"}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">AWB</div>
                        <div className="mt-1 font-mono text-neutral-800">{o.tracking?.awb || o.shiprocket?.awb_code || <span className="text-neutral-400">Pending</span>}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Status</div>
                        <div className="mt-1 text-neutral-800">{o.tracking?.current_status || <span className="text-neutral-400">Not fetched</span>}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">ETA</div>
                        <div className="mt-1 text-neutral-800">{o.tracking?.eta || "—"}</div>
                      </div>
                      <div className="flex items-end justify-end gap-2">
                        {o.tracking?.tracking_url && (
                          <a
                            href={o.tracking.tracking_url}
                            target="_blank" rel="noreferrer"
                            data-testid={`track-link-${o.id}`}
                            className="text-[#E05A47] hover:underline underline-offset-4"
                          >
                            Live track ↗
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`refresh-tracking-${o.id}`}
                          onClick={() => refreshTracking(o.id)}
                          disabled={busy[o.id] === "track"}
                          className="rounded-full h-8 text-xs"
                        >
                          <RefreshCw size={12} className={`mr-1.5 ${busy[o.id] === "track" ? "animate-spin" : ""}`}/>
                          Refresh
                        </Button>
                      </div>
                      <div className="md:col-span-4 text-[10px] text-neutral-400 font-mono">
                        Shipment #{o.shiprocket.shipment_id} · Order #{o.shiprocket.order_id}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {o.razorpay_payment_id && (
                <div className="mt-3 text-[11px] text-neutral-400 font-mono flex items-center gap-1">
                  <ExternalLink size={10}/> {o.razorpay_payment_id}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? "border-[#E05A47] bg-[#FDF3F1]" : "border-neutral-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">{label}</div>
      <div className={`mt-2 font-serif text-3xl ${accent ? "text-[#E05A47]" : "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { bg: "bg-[#EAF3E6]", text: "text-[#4A6741]", label: "Paid" },
    cod_pending: { bg: "bg-[#FEF3E2]", text: "text-[#8a5a1e]", label: "COD · pending" },
    created: { bg: "bg-neutral-100", text: "text-neutral-600", label: "Pending" },
    signature_failed: { bg: "bg-red-50", text: "text-red-700", label: "Signature failed" },
  };
  const s = map[status] || { bg: "bg-neutral-100", text: "text-neutral-600", label: status };
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}
